import React, { useState, useEffect } from 'react';
import { admin } from '../api';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await admin.getAllUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users', err);
      if (err.response?.status === 403) {
        setError('Доступ запрещен. Требуются права администратора.');
      } else {
        setError('Ошибка загрузки пользователей: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId, userName) => {
    setShowDeleteConfirm({ userId, userName });
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) {
      console.warn('showDeleteConfirm is null');
      return;
    }

    const { userId } = showDeleteConfirm;
    setDeletingUserId(userId);
    setError(null);

    try {
      const response = await admin.deleteUser(userId);
      
      // Перезагружаем список пользователей
      await loadUsers();
      setShowDeleteConfirm(null);
      setDeletingUserId(null);
    } catch (err) {
      console.error('Failed to delete user', err);
      setError('Ошибка удаления пользователя: ' + (err.response?.data?.detail || err.message || 'Неизвестная ошибка'));
      setDeletingUserId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBalance = (balance) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(balance);
  };

  if (loading) {
    return (
      <>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-gray-600">Загрузка...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Админ панель</h1>
          <p className="text-gray-600 mt-2">Управление пользователями системы</p>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
          <span>{error}</span>
          <button onClick={loadUsers} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Админ панель</h1>
        <p className="text-gray-600 mt-2">Управление пользователями системы</p>
      </div>

      {showDeleteConfirm && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={handleDeleteCancel}
          />
          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border-2 border-yellow-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Подтверждение удаления
              </h3>
              <p className="text-gray-700 mb-2">
                Вы уверены, что хотите удалить пользователя <strong>"{showDeleteConfirm.userName}"</strong>?
              </p>
              <p className="text-gray-600 text-sm mb-6">
                Все приглашения и базы вопросов этого пользователя будут архивированы.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deletingUserId === showDeleteConfirm.userId}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Отмена
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deletingUserId === showDeleteConfirm.userId}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingUserId === showDeleteConfirm.userId ? 'Удаление...' : 'Удалить'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название компании
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Баланс
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата регистрации
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Пользователи не найдены
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {user.name}
                        {user.is_vip && (
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800" title="VIP пользователь">
                            VIP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatBalance(user.balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteClick(user.id, user.name)}
                        disabled={deletingUserId === user.id || showDeleteConfirm?.userId === user.id}
                        className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Всего пользователей: <strong>{users.length}</strong>
      </div>
    </>
  );
}

export default AdminPanel;

